import {
  type BlobArrayLike,
  BlobCreate,
  type BlobInput,
  type BlobLike,
} from "@pdfking/compatibility"
import type { Bytes } from "@pdfking/tfunc"
import { IsArray, IsString, UInt8Merge } from "@pdfking/tfunc"

const { max, min } = Math

type BlobState = Pick<BlobArrayLike, "blobs" | "offsets" | "size">

// Binary search to find blob index containing position
const findBlobIndex = (
  blobs: BlobLike[],
  offsets: number[],
  pos: number
): number => {
  let lo = 0
  let hi = blobs.length
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if (offsets[mid] + blobs[mid].size <= pos) lo = mid + 1
    else hi = mid
  }
  return lo
}

const concatBuffers = (buffers: ArrayBuffer[], len: Bytes): ArrayBuffer =>
  buffers.length === 1
    ? buffers[0]
    : (UInt8Merge(buffers, len).buffer as ArrayBuffer)

const clear = (state: BlobState) => {
  state.blobs.length = 0
  state.offsets.length = 0
  state.size = 0
}

const add = (state: BlobState, blob: BlobLike) => {
  state.blobs.push(blob)
  state.offsets.push(state.size)
  state.size += blob.size
}

const merge = (state: BlobState, inputs: BlobInput): BlobState => {
  for (const v of IsArray(inputs) ? inputs : [inputs]) {
    if (IsString(v)) {
      add(state, BlobCreate([v]))
    } else if ("offsets" in v) {
      for (const b of v.blobs) add(state, b)
    } else if (
      v instanceof ArrayBuffer ||
      v instanceof Uint8Array ||
      v instanceof Blob
    ) {
      add(state, BlobCreate(v))
    } else {
      add(state, v)
    }
  }
  return state
}

const blankState = (): BlobState => ({ blobs: [], offsets: [], size: 0 })

const arrayBuffer = ({ blobs, size }: BlobState): Promise<ArrayBuffer> =>
  Promise.all(blobs.map(b => b.arrayBuffer())).then(b =>
    concatBuffers(b, size as Bytes)
  )

const arrayBufferSync = ({ blobs, size }: BlobState): ArrayBuffer =>
  concatBuffers(
    blobs.map(b => b.arrayBufferSync()),
    size as Bytes
  )

const slice = (
  { blobs, offsets, size }: BlobState,
  type: string,
  start: number,
  end: number
): BlobArrayLike => {
  if (start < 0) start = max(0, size + start)
  if (end < 0) end = max(0, size + end)
  end = min(end, size)
  if (start >= end || start >= size) {
    return createBlobArray(blankState(), type)
  }

  const startIdx = findBlobIndex(blobs, offsets, start)
  const endIdx = findBlobIndex(blobs, offsets, end - 1)

  const result = blankState()
  for (let i = startIdx; i <= endIdx; i++) {
    const blob = blobs[i]
    const blobStart = offsets[i]
    add(
      result,
      blob.slice(max(0, start - blobStart), min(blob.size, end - blobStart))
    )
  }

  return createBlobArray(result, type)
}

const createBlobArray = (state: BlobState, type = ""): BlobArrayLike => {
  const result: BlobArrayLike = {
    ...state,
    type,
    slice: (start = 0, end = state.size) => slice(result, type, start, end),
    arrayBuffer: () => arrayBuffer(result),
    arrayBufferSync: () => arrayBufferSync(result),
    push: inputs => merge(result, inputs),
    clear: () => clear(result),
    createObjectURL: () =>
      arrayBuffer(result).then(b => BlobCreate(b, type).createObjectURL()),
  }
  return result
}

export const BlobArray = (
  inputs: BlobInput = [],
  type = "application/octet-stream"
): BlobArrayLike => createBlobArray(merge(blankState(), inputs), type)
